/**
 * All logic behind the Form Flows list: paging query, selection-driven toolbar state,
 * create / edit / duplicate / delete / archive / add-to-library, and the add-edit dialog.
 *
 * Platform-agnostic on purpose: written to be consumed by `packages/mobile` as well as
 * `packages/web`, so it returns state, handlers and option lists only — never markup. The platform
 * owns rendering and passes in the few things it alone can do: how to read the current selection,
 * how to refresh the list, where "back" goes, and the list endpoint.
 */
import { useTranslation, untranslated } from "@i18n";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useAppDispatch as useDispatch } from "@Reducers/hooks";
import _ from "lodash";
import { formFlowController, getTemplates } from "@Actions/Forms";
import { showToastNotification, type ToastNotificationPayload } from "@Reducers/toastSlice";
import { TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type { RootState } from "@Reducers/index";
import type { FormFlowControllerRequest } from "@ServerTypes/forms/forms.schema";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import {
  buildFormFlowListQuery,
  DEFAULT_USER_SELECTION_MODE,
  FORM_FLOW_CREATE_MENU_IDS,
  FORM_FLOW_LIBRARY_STEPS,
  FORM_FLOW_TOOLBAR_IDS,
  getArchivedToolbarOptions,
  getFormFlowCreateMenuItems,
  getFormFlowListColumns,
  getFormFlowToolbarOptions,
  getInitialFormFlowFormValues,
  getToolBtnStateForSelection,
  getUserSelectionModeOptions,
  NO_SELECTION_TOOL_BTN_STATE,
} from "./formFlow.constants";
import type { AddFromLibraryState, FormFlowFormValues, FormFlowRow, FormFlowToolBtnState } from "./formFlow.types";

/**
 * One Form template as the add/edit dialog's dropdown sees it.
 *
 * Declared here because `getTemplatesResponse` types its rows as `result: z.array(z.any())` —
 * there is no server row type to import. Only the two fields the dropdown binds to
 * (`fields={{ text: "name", value: "_id" }}`) are named; do not grow this into a template type.
 *
 * A plain type alias, not an interface, so it keeps the implicit index signature that
 * `SelectDropDown`'s `options` prop requires (`OptionBase = Record<string, unknown>`).
 */
type FormTemplateOption = {
  _id?: string;
  name?: string;
};

/**
 * What the flows list pages against: either the default project query built by
 * `buildFormFlowListQuery`, or the library query a caller substitutes through `customQuery`
 * (`buildFormFlowLibraryQuery`, a completely different shape). The open index signature is what
 * lets the category-sync effect below spread a query of either shape.
 */
type FormFlowListQuery = Partial<ReturnType<typeof buildFormFlowListQuery>> & { [key: string]: unknown };

/**
 * Payload the add/edit dialog sends. `formFlowController` is constrained to
 * `Exact<FormFlowControllerRequest, …>`, but this object is assembled field-by-field from dialog
 * state — `_id` is attached only in edit mode and `data` is the dialog's own value bag — so it is
 * asserted at the call site rather than built as a discriminated union member. The bytes on the
 * wire are unchanged.
 */
interface FormFlowDialogPayload {
  action: "save" | "update";
  _id?: string;
  data: FormFlowFormValues & { spaceId: string; userSelectionMode: string };
}

interface UseFormFlowListParams {
  projectId?: string;
  /** Category the list is scoped to. Web reads it from the `resourceType` search param. */
  formCategory?: string;
  /** Replaces the default project query (used by the library picker). */
  customQuery?: unknown;
  /**
   * Endpoint the list pages against, supplied by the platform. Kept as a param rather than
   * imported here: `@Services/dataManager` builds its URLs at module-load time, which is unsafe
   * before the store holds a backend URL (notably on mobile).
   */
  serviceUrl?: string;
  /** Replaces `serviceUrl` (used by the library picker). */
  customService?: string;
  /** Selection-only mode: no toolbar, no header, no toolbar-driven state. */
  showOnlyGrid?: boolean;
  /** Lets an embedding wizard enable its own Next button from this list's selection. */
  setNextBtnState?: (state: boolean) => void;
  /** Opens a flow for editing. */
  setFormFlowId?: (formFlowId: string) => void;
  /** Current selection of the active list, as row data. */
  getSelectedRows?: () => FormFlowRow[];
  /** Current selection of the archived list, as row data. */
  getArchivedSelectedRows?: () => FormFlowRow[];
  /** Re-fetch the active list. */
  refreshList?: () => void;
  /** Re-fetch the archived list. */
  refreshArchivedList?: () => void;
  /** Where the header back arrow goes. */
  onBack?: () => void;
}

function useFormFlowList({
  projectId,
  formCategory,
  customQuery,
  serviceUrl,
  customService,
  showOnlyGrid = false,
  setNextBtnState,
  setFormFlowId,
  getSelectedRows,
  getArchivedSelectedRows,
  refreshList,
  refreshArchivedList,
  onBack,
}: UseFormFlowListParams) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getProjectSettingObject } = useSelector((state: RootState) => state);

  const [spaceId, setSpaceId] = useState<string>("");
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [openAddEditModal, setOpenAddEditModal] = useState(false);
  const [addFromLibrary, setAddFromLibrary] = useState<AddFromLibraryState>({
    state: false,
    step: FORM_FLOW_LIBRARY_STEPS.SELECT_FLOWS,
  });
  const [userSelectionMode, setUserSelectionMode] = useState<string>(DEFAULT_USER_SELECTION_MODE);
  const [toolBtnState, setToolBtnState] = useState<FormFlowToolBtnState>({ ...NO_SELECTION_TOOL_BTN_STATE });
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archivedToolBtnState, setArchivedToolBtnState] = useState({ unarchive: false });
  const [showIllustration, setShowIllustration] = useState(false);
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const [archivedShowIllustration, setArchivedShowIllustration] = useState(false);
  const [archivedIsLoadingComplete, setArchivedIsLoadingComplete] = useState(false);

  const initialFormValues = getInitialFormFlowFormValues({ formCategory, projectId });
  const [formFlowData, setFormFlowData] = useState<FormFlowFormValues>(initialFormValues);
  const [formTemplates, setFormTemplates] = useState<FormTemplateOption[]>([]);

  /** Whether the current user may manage flows at all. */
  const hasAddPermission = _.get(getProjectSettingObject, "formFlow.add.enable", false);

  /* ---------------- templates for the add/edit dialog ---------------- */

  const fetchFormTemplates = useCallback(async () => {
    if (formFlowData.formCategory) {
      const response = await getTemplates({
        value: {
          resourceType: formFlowData.formCategory,
          isArchived: false,
          // Ids reaching the client are already validated ObjectIds; the request schemas brand them.
          projectId: projectId as ObjectIdString | undefined,
        },
      });
      if (response.data?.result) {
        setFormTemplates(response.data.result);
      }
    }
  }, [formFlowData.formCategory, projectId]);

  useEffect(() => {
    if (openAddEditModal) {
      fetchFormTemplates();
    }
  }, [openAddEditModal, fetchFormTemplates]);

  /* ---------------- list queries ---------------- */

  const [listQuery, setListQuery] = useState<FormFlowListQuery>(
    (customQuery as FormFlowListQuery) ||
      buildFormFlowListQuery({ projectId, formCategory: formFlowData.formCategory, isArchived: false }),
  );

  /** Remote source the list pages against. */
  const listService = customService || serviceUrl;

  // Stable-reference query for the archived list (only archived flows for this project + category).
  const archivedListQuery = useMemo(
    () => buildFormFlowListQuery({ projectId, formCategory: formFlowData.formCategory, isArchived: true }),
    [projectId, formFlowData.formCategory],
  );

  // Keep the project query pointed at the selected category. A caller-supplied `customQuery` owns
  // the query outright, so this is a no-op there and the effect below applies theirs instead.
  useEffect(() => {
    if (!customQuery) {
      setListQuery((prev: FormFlowListQuery) => ({
        ...prev,
        formCategory: formFlowData.formCategory,
      }));
    }
  }, [formFlowData.formCategory, customQuery]);

  useEffect(() => {
    if (customQuery) {
      setListQuery(customQuery as FormFlowListQuery);
    }
  }, [customQuery]);

  /* ---------------- selection ---------------- */

  const readSelectedRows = (): FormFlowRow[] => getSelectedRows?.() ?? [];
  const readArchivedSelectedRows = (): FormFlowRow[] => getArchivedSelectedRows?.() ?? [];

  /** Call with the number of selected rows whenever the active list's selection changes. */
  const handleSelectionChange = (selectedCount: number) => {
    if (setNextBtnState) {
      setNextBtnState(selectedCount > 0);
    }
    if (showOnlyGrid) {
      return;
    }
    setToolBtnState(getToolBtnStateForSelection(selectedCount));
  };

  /** Call with the number of selected rows whenever the archived list's selection changes. */
  const handleArchivedSelectionChange = (selectedCount: number) => {
    setArchivedToolBtnState({ unarchive: selectedCount >= 1 });
  };

  /* ---------------- refresh ---------------- */

  const refreshGrid = (isArchivedGrid = false) => {
    if (isArchivedGrid) {
      refreshArchivedList?.();
    } else {
      refreshList?.();
    }
  };

  /* ---------------- dialogs ---------------- */

  const closeDeleteModal = () => {
    setShowDeleteConfirmationModal(false);
  };

  const closeAddEditModal = () => {
    setOpenAddEditModal(false);
    setEditMode(false);
    setFormFlowData((prev: FormFlowFormValues) => ({
      ...getInitialFormFlowFormValues({ formCategory, projectId }),
      formCategory: prev.formCategory,
    }));
  };

  const closeArchiveModal = () => {
    setShowArchiveModal(false);
    setArchivedToolBtnState({ unarchive: false });
    // Refresh the active list so any rows unarchived while the modal was open reappear.
    refreshList?.();
  };

  /**
   * Handles a pick from the split "Create" button. `menuId` is optional because the web dropdown's
   * `SelectEventArgs["item"].id` is — neither branch matches when it is absent, which is what
   * already happened.
   */
  const handleCreateMenuSelect = (menuId?: string) => {
    if (menuId === FORM_FLOW_CREATE_MENU_IDS.CREATE) {
      setOpenAddEditModal(true);
    } else if (menuId === FORM_FLOW_CREATE_MENU_IDS.CREATE_FROM_LIBRARY) {
      setAddFromLibrary((prev) => ({ ...prev, state: true }));
    }
  };

  const openCreateModal = () => setOpenAddEditModal(true);
  const openLibraryModal = () => setAddFromLibrary((prev) => ({ ...prev, state: true }));
  const openArchiveModal = () => setShowArchiveModal(true);

  const handleCancelLibrary = () => {
    setAddFromLibrary(() => ({ state: false, step: FORM_FLOW_LIBRARY_STEPS.SELECT_FLOWS }));
    setUserSelectionMode(DEFAULT_USER_SELECTION_MODE);
    if (setNextBtnState) {
      setNextBtnState(false);
    }
  };

  /* ---------------- mutations ---------------- */

  const handleDeleteFormFlow = async () => {
    const selectedData = readSelectedRows();
    // `FormFlowRow._id` is an unbranded optional string because the list response is
    // `result: z.array(z.any())`; the ids come straight from the server, so they are asserted
    // (erased at emit) rather than re-validated here.
    const formFlowIds: ObjectIdString[] = selectedData.map((data: FormFlowRow) => data._id as ObjectIdString);

    const res = await formFlowController({
      action: "delete" as const,
      formFlowIds,
      projectId: projectId as ObjectIdString,
    });
    let toastContent: ToastNotificationPayload;
    if (res.data.status) {
      toastContent = {
        content: t("Selected Form Flows have been deleted successfully"),
        type: TOAST_TYPE_SUCCESS,
      };
      refreshGrid();
    } else {
      toastContent = {
        content: untranslated(res.data.errorMessage),
        type: TOAST_TYPE_ERROR,
      };
    }
    dispatch(showToastNotification(toastContent));
    closeDeleteModal();
  };

  /** Archives (`isArchived=true`) or unarchives (`isArchived=false`) the given rows. */
  const handleArchiveFormFlow = async (selectedData: FormFlowRow[], isArchived: boolean) => {
    const formFlowIds: ObjectIdString[] = selectedData.map((data: FormFlowRow) => data._id as ObjectIdString);
    if (!formFlowIds.length) return;

    const res = await formFlowController({
      action: "archive",
      formFlowIds,
      isArchived,
      projectId: projectId as ObjectIdString,
    });

    let toastContent: ToastNotificationPayload;
    if (res.data.status) {
      toastContent = {
        content: isArchived
          ? t("Selected Form Flows have been archived successfully")
          : t("Selected Form Flows have been unarchived successfully"),
        type: TOAST_TYPE_SUCCESS,
      };
      // Refresh the list the rows just left.
      refreshGrid(!isArchived);
    } else {
      toastContent = {
        content: untranslated(res.data.errorMessage),
        type: TOAST_TYPE_ERROR,
      };
    }
    dispatch(showToastNotification(toastContent));
  };

  const handleDuplicateFormFlow = async (selectedRow: FormFlowRow) => {
    const res = await formFlowController({
      action: "duplicate" as const,
      sourceFormFlowId: selectedRow._id as ObjectIdString,
    });
    if (res.data.status) {
      dispatch(
        showToastNotification({
          content: t("Form flow duplicated successfully"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    }
    refreshGrid();
  };

  const handleAddToLibrary = async (selectedRows: FormFlowRow[]) => {
    const res = await formFlowController({
      action: "addToLibrary",
      formFlowIds: selectedRows.map((data: FormFlowRow) => data._id as ObjectIdString),
    });
    let toastContent: ToastNotificationPayload = {
      content: t("Selected Form Flows have been added to library successfully"),
      type: TOAST_TYPE_SUCCESS,
    };
    if (!res.data.status) {
      toastContent = {
        content: untranslated(res.data.errorMessage),
        type: TOAST_TYPE_ERROR,
      };
    }
    refreshGrid();
    dispatch(showToastNotification(toastContent));
  };

  /** Seeds the add/edit dialog from the selected row and opens it in edit mode. */
  const startEditFormFlow = (selectedRow: FormFlowRow) => {
    setFormFlowData({
      // `FormFlowRow` marks every column optional because the list response is
      // `result: z.array(z.any())`, not because a flow may lack them: `name`/`formCategory` are
      // required on the save schema and `selectedForm` is the template the flow is built on. The
      // assertions are erased at emit and keep the unconditional reads this code already did.
      name: selectedRow.name as string,
      selectedTemplate: selectedRow.selectedForm!._id,
      formCategory: selectedRow.formCategory as string,
      projectId,
    });
    setOpenAddEditModal(true);
    setEditMode(true);
  };

  /** Routes a toolbar id of the active list to its action. */
  const toolbarClickHandler = async (toolbarId: string) => {
    if (!getSelectedRows) return;
    const totalRowsSelected = readSelectedRows();
    // Edit and Duplicate — the only two branches that read this — are enabled only for a
    // single-row selection (`getToolBtnStateForSelection`), so row 0 exists. Erased at emit.
    const selectedData = totalRowsSelected[0]!;
    if (toolbarId === FORM_FLOW_TOOLBAR_IDS.DELETE) {
      setShowDeleteConfirmationModal(true);
    }
    if (toolbarId === FORM_FLOW_TOOLBAR_IDS.ARCHIVE) {
      handleArchiveFormFlow(totalRowsSelected, true);
    }
    if (toolbarId === FORM_FLOW_TOOLBAR_IDS.EDIT) {
      startEditFormFlow(selectedData);
    }
    if (toolbarId === FORM_FLOW_TOOLBAR_IDS.DUPLICATE) {
      await handleDuplicateFormFlow(selectedData);
    }
    if (toolbarId === FORM_FLOW_TOOLBAR_IDS.ADD_TO_LIBRARY) {
      await handleAddToLibrary(totalRowsSelected);
    }
  };

  /** Routes a toolbar id of the archived list to its action. */
  const archivedToolbarClickHandler = async (toolbarId: string) => {
    if (!getArchivedSelectedRows) return;
    if (toolbarId === FORM_FLOW_TOOLBAR_IDS.UNARCHIVE) {
      handleArchiveFormFlow(readArchivedSelectedRows(), false);
    }
  };

  /* ---------------- add / edit dialog ---------------- */

  /**
   * Updates one field of the add/edit dialog. The template dropdown hands over the whole option
   * object, but only its `_id` is stored: `selectedTemplate` is an ObjectId string in the form-flow
   * save/update schema, so sending the object fails validation.
   */
  const setFormFlowField = (name: string, value: unknown) => {
    if (name === "selectedTemplate") {
      setFormFlowData((prev: FormFlowFormValues) => ({ ...prev, [name]: (value as FormTemplateOption)._id }));
      return;
    }
    setFormFlowData((prev: FormFlowFormValues) => ({ ...prev, [name]: value }));
  };

  /** Adapter for web inputs that emit a `{ target: { name, value } }` change event. */
  const handleChange = (event: { target: { name: string; value: unknown } }) => {
    setFormFlowField(event.target.name, event.target.value);
  };

  const saveFromFlow = async (data: FormFlowDialogPayload) => {
    const res = await formFlowController(data as FormFlowControllerRequest);
    if (res.data.status) {
      const content = editMode
        ? t("Form flow updated successfully")
        : t('New Form Flow "{{name}}" has been created successfully', { name: formFlowData.name });
      dispatch(
        showToastNotification({
          content,
          type: TOAST_TYPE_SUCCESS,
        }),
      );
      setFormFlowData((prev: FormFlowFormValues) => ({
        ...getInitialFormFlowFormValues({ formCategory, projectId }),
        formCategory: prev.formCategory,
      }));
      closeAddEditModal();
      refreshGrid();
      if (setNextBtnState) {
        setNextBtnState(false);
      }
    } else {
      dispatch(
        showToastNotification({
          content: untranslated(res.data.errorMessage),
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const handleCreateFormFlowClick = async () => {
    const data: FormFlowDialogPayload = {
      data: { ...formFlowData, spaceId, userSelectionMode },
      action: editMode ? "update" : "save",
    };
    if (editMode) {
      // Edit mode is only reachable from a single-row selection, so row 0 exists. Erased at emit.
      const selectedData = readSelectedRows()[0]!;
      data._id = selectedData._id;
    }
    await saveFromFlow(data);
  };

  /** Create/Update stays disabled until the dialog has everything the server needs. */
  const isSaveDisabled = editMode
    ? formFlowData?.name?.trim() === ""
    : formFlowData?.name?.trim() === "" ||
      formFlowData?.formCategory === "" ||
      !spaceId ||
      !formFlowData.selectedTemplate;

  const handleUserSelectionModeChange = (args: { target: { value: string } }) => {
    setUserSelectionMode(args.target.value);
  };

  /* ---------------- rows ---------------- */

  /**
   * Opens the clicked flow. `cell` is the web list's column index — clicking the checkbox
   * column (0) only toggles selection, so it must not open the flow. Platforms without cells
   * simply omit it.
   */
  const handleRecordClick = ({ rowData, cell }: { rowData?: FormFlowRow; cell?: number }) => {
    if (cell !== 0 && setFormFlowId && rowData) {
      // `_id` is optional on `FormFlowRow` only because the list response is
      // `result: z.array(z.any())`; every persisted flow has one. Erased at emit.
      setFormFlowId(rowData._id as string);
    }
  };

  /**
   * Signals list-ready with the loaded row count so each list can toggle its own empty-state
   * illustration. `dataCount` is the post-load total: 0 ⇔ the list is empty — including after a
   * refresh triggered by delete/archive. Do NOT read the loaded dataset instead: on web, React
   * has not synced the new rows into the DataView when this fires, so it still holds the
   * pre-refresh rows and would wrongly suppress the illustration.
   */
  const handleListLoaded = (dataCount?: number) => {
    setShowIllustration((dataCount ?? 0) === 0);
    setIsLoadingComplete(true);
  };

  const handleArchivedListLoaded = (dataCount?: number) => {
    setArchivedShowIllustration((dataCount ?? 0) === 0);
    setArchivedIsLoadingComplete(true);
  };

  const handleBackClick = () => {
    onBack?.();
  };

  /* ---------------- option lists ---------------- */

  const columns = useMemo(() => getFormFlowListColumns(), []);
  const toolbarOptions = getFormFlowToolbarOptions(toolBtnState);
  const archivedToolbarOptions = getArchivedToolbarOptions(archivedToolBtnState);
  const createMenuItems = getFormFlowCreateMenuItems();
  const userSelectionModeOptions = getUserSelectionModeOptions();

  return {
    // permissions
    hasAddPermission,

    // list wiring
    listQuery,
    listService,
    archivedListQuery,
    columns,
    toolbarOptions,
    archivedToolbarOptions,
    createMenuItems,
    userSelectionModeOptions,

    // empty-state / loading flags
    isLoadingComplete,
    showIllustration,
    archivedIsLoadingComplete,
    archivedShowIllustration,
    handleListLoaded,
    handleArchivedListLoaded,

    // selection
    toolBtnState,
    archivedToolBtnState,
    handleSelectionChange,
    handleArchivedSelectionChange,
    handleRecordClick,

    // dialog visibility
    openAddEditModal,
    setOpenAddEditModal,
    showDeleteConfirmationModal,
    setShowDeleteConfirmationModal,
    showArchiveModal,
    setShowArchiveModal,
    addFromLibrary,
    setAddFromLibrary,
    openCreateModal,
    openLibraryModal,
    openArchiveModal,
    closeAddEditModal,
    closeDeleteModal,
    closeArchiveModal,
    handleCancelLibrary,
    handleCreateMenuSelect,

    // add / edit dialog
    editMode,
    formFlowData,
    formTemplates,
    spaceId,
    setSpaceId,
    handleChange,
    setFormFlowField,
    isSaveDisabled,
    handleCreateFormFlowClick,
    userSelectionMode,
    setUserSelectionMode,
    handleUserSelectionModeChange,

    // actions
    toolbarClickHandler,
    archivedToolbarClickHandler,
    handleDeleteFormFlow,
    handleArchiveFormFlow,
    handleDuplicateFormFlow,
    handleAddToLibrary,
    startEditFormFlow,
    refreshGrid,
    handleBackClick,
  };
}

export default useFormFlowList;
